// Avtaleoversikt (/avtaler), i kontrollpanelets stil (planer/ny-kundesamhandling-plan.md): sidetopp
// med ikon, søk og filtre som knapper, og avtalene som store kort med logo. Kortet åpner avtalen.
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TYPES, TYPE_LABELS, getAgreementStatus, formatDate } from "../constants/avtaler";
import AvtaleLogo from "../components/AvtaleLogo";
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, Valgknapper, feltKlasse, knappPrimar, knappSekundar } from "../ny-kundesamhandling/Skjema";
import { FARGER } from "../ny-kundesamhandling/handlinger";
import Ikon from "../ny-kundesamhandling/Ikon";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const STATUS_FILTERS = [
  { key: "", label: "Alle" },
  { key: "AKTIV", label: "Aktiv" },
  { key: "UTLOPER_SNART", label: "Utløper snart" },
  { key: "UTLOPT", label: "Utløpt" },
  { key: "LOPENDE", label: "Løpende" },
];

// Samme merkestil som ordrene (rund pille), med farge etter status.
const STATUS_FARGE = {
  AKTIV: "bg-emerald-100 text-emerald-900",
  UTLOPER_SNART: "bg-amber-100 text-amber-950",
  UTLOPT: "bg-red-100 text-red-900",
  LOPENDE: "bg-slate-200 text-slate-800",
};

const merke = "inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-semibold";

// Avtaler som kan faktureres, merket grønt (huket av i skjemaet eller på avtalen).
function FakturaMerke() {
  return (
    <span className={`${merke} inline-flex items-center gap-1 bg-green-700 text-white`}>
      <Ikon navn="hake" className="w-4 h-4" /> Fakturakunde
    </span>
  );
}

function StatusMerke({ expiryDate }) {
  const status = getAgreementStatus(expiryDate);
  return <span className={`${merke} ${STATUS_FARGE[status.key]}`}>{status.label}</span>;
}

function NewAgreementForm({ onCreated, onCancel }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("KLUBB");
  const [discountDetails, setDiscountDetails] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [fakturakunde, setFakturakunde] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feil, setFeil] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return setFeil("Skriv inn navnet på klubben, bedriften eller personen.");
    if (!discountDetails.trim()) return setFeil("Skriv inn rabattbetingelsene.");
    setSaving(true);
    setFeil("");
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, type, discountDetails, discountCode, contactInfo, contactPhone, startDate, expiryDate, fakturakunde }),
      });
      if (!r.ok) throw new Error();
      onCreated(await r.json());
    } catch {
      setFeil("Kunne ikke opprette avtalen. Prøv igjen om litt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-labelledby="av-ny-tittel" className="space-y-3 rounded-2xl border-2 border-sky-200 bg-sky-50 p-3 sm:p-3">
      <h2 id="av-ny-tittel" className="text-lg font-bold text-slate-900">Ny avtale</h2>
      <div className="grid gap-3 md:grid-cols-3">
        <Tekstfelt id="av-navn" etikett="Klubb, bedrift eller person" value={name} onChange={(e) => setName(e.target.value)} required className="md:col-span-2" />
        <Valgfelt id="av-type" etikett="Type" value={type} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </Valgfelt>
      </div>
      <Tekstfelt
        id="av-rabatt"
        etikett="Rabattbetingelser"
        hjelp="F.eks. -20 % på fotballutstyr, -15 % på resten av butikken (gjelder ikke elektronikk)."
        flerlinjet
        value={discountDetails}
        onChange={(e) => setDiscountDetails(e.target.value)}
        required
      />
      <div className="grid gap-3 md:grid-cols-2">
        <Tekstfelt id="av-kode" etikett="Rabattkode (valgfritt)" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} />
        <Tekstfelt id="av-kontakt" etikett="Kontaktperson (valgfritt)" value={contactInfo} onChange={(e) => setContactInfo(e.target.value)} />
        <Tekstfelt id="av-telefon" etikett="Telefon til kontaktperson (valgfritt)" type="tel" autoComplete="off" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
        <Tekstfelt id="av-start" etikett="Inngått" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <Tekstfelt id="av-slutt" etikett="Utløper" hjelp="La stå tom for løpende avtale." type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
      </div>
      <label htmlFor="av-faktura" className="flex items-center gap-2 font-semibold text-slate-800">
        <input id="av-faktura" type="checkbox" checked={fakturakunde} onChange={(e) => setFakturakunde(e.target.checked)} className="h-5 w-5 accent-green-700" />
        Fakturakunde (kan faktureres)
      </label>
      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={saving} className={knappPrimar}>
          <Ikon navn="hake" className="w-5 h-5" /> {saving ? "Lagrer …" : "Opprett avtale"}
        </button>
        <button type="button" onClick={onCancel} className={knappSekundar}>Avbryt</button>
      </div>
    </form>
  );
}

// Det som ikke gir rabatt på noen avtale. Står over avtalene, i samme stil som notisen på kontrollpanelet.
function ExclusionBanner({ settings, onSettingsUpdated }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(settings.exclusionText);
  const [saving, setSaving] = useState(false);
  const [feil, setFeil] = useState("");

  async function handleSave() {
    if (!text.trim()) return setFeil("Unntaksteksten kan ikke være tom.");
    setSaving(true);
    setFeil("");
    try {
      const r = await fetch(`${API_BASE_URL}/avtaler/innstillinger`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exclusionText: text }),
      });
      if (!r.ok) throw new Error();
      onSettingsUpdated(await r.json());
      setEditing(false);
    } catch {
      setFeil("Kunne ikke lagre unntaksteksten. Prøv igjen om litt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <aside aria-labelledby="av-unntak" className="flex flex-wrap items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-slate-800">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${FARGER.betaling.ikon}`}>
        <Ikon navn="varsel" className="w-6 h-6" />
      </span>
      <div className="min-w-0 flex-1 basis-60 space-y-1">
        <h2 id="av-unntak" className="font-bold text-slate-900">Gjelder ikke på (alle avtaler)</h2>
        {editing ? (
          <div className="space-y-2">
            <label htmlFor="av-unntak-tekst" className="sr-only">Unntak for alle avtaler</label>
            <textarea id="av-unntak-tekst" value={text} onChange={(e) => setText(e.target.value)} rows={3} className={feltKlasse} />
            {feil && <p role="alert" className="text-red-800">{feil}</p>}
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handleSave} disabled={saving} className={knappPrimar}>{saving ? "Lagrer …" : "Lagre"}</button>
              <button type="button" onClick={() => { setEditing(false); setFeil(""); }} className={knappSekundar}>Avbryt</button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap">{settings.exclusionText}</p>
        )}
      </div>
      {!editing && (
        <button
          type="button"
          onClick={() => { setText(settings.exclusionText); setEditing(true); }}
          className="inline-flex min-h-[2.25rem] shrink-0 items-center gap-2 rounded-xl border-2 border-amber-300 bg-white px-3 py-1.5 font-semibold text-amber-950 hover:bg-amber-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
        >
          <Ikon navn="blyant" className="w-5 h-5" /> Endre
        </button>
      )}
    </aside>
  );
}

function AgreementCard({ agreement, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(agreement._id)}
      className={`group flex h-full w-full flex-col gap-2 rounded-2xl border-2 p-3 text-left transition hover:border-sky-300 hover:bg-sky-50/50 ${agreement.fakturakunde ? "border-green-600 bg-green-50" : "border-slate-200 bg-white"} active:scale-[0.99] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50`}
    >
      <span className="flex items-start gap-2">
        <AvtaleLogo agreement={agreement} />
        <span className="min-w-0 flex-1">
          <span lang="no" className="block text-base font-bold leading-tight text-slate-900 break-words hyphens-auto">{agreement.name}</span>
          <span className="mt-1.5 flex flex-wrap gap-1.5">
            <span className={`${merke} bg-sky-100 text-sky-900`}>{TYPE_LABELS[agreement.type] || agreement.type}</span>
            <StatusMerke expiryDate={agreement.expiryDate} />
            {agreement.fakturakunde && <FakturaMerke />}
          </span>
        </span>
      </span>

      <span className="block whitespace-pre-wrap text-slate-700 line-clamp-3 [overflow-wrap:anywhere]">{agreement.discountDetails}</span>

      {agreement.discountCode && (
        <span className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          Rabattkode
          <span className="rounded-lg bg-slate-100 px-2 py-0.5 font-mono font-semibold text-slate-900 [overflow-wrap:anywhere]">{agreement.discountCode}</span>
        </span>
      )}

      <span className="mt-auto flex items-center justify-between gap-2 text-sm text-slate-600">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {agreement.expiryDate ? `Utløper ${formatDate(agreement.expiryDate)}` : "Uten sluttdato"}
          {agreement.images?.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Ikon navn="dokument" className="w-4 h-4" /> Kontrakt
            </span>
          )}
        </span>
        <Ikon navn="pil" className="w-5 h-5 shrink-0 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
      </span>
    </button>
  );
}

// Treff på navn i ansattlistene (f.eks. Røykenbadet), vist over avtalekortene når man søker.
function AnsattTreff({ treff, avtaler, onOpen }) {
  if (!treff.length) return null;
  const navnPaAvtale = new Map(avtaler.map((a) => [a._id, a.name]));
  return (
    <section aria-labelledby="av-ansatt-treff" className="space-y-2 rounded-2xl border-2 border-green-200 bg-green-50 p-3">
      <h2 id="av-ansatt-treff" className="font-bold text-slate-900">Står på en ansattliste ({treff.length})</h2>
      <ul className="divide-y divide-green-200">
        {treff.map((t) => (
          <li key={t._id}>
            <button
              type="button"
              onClick={() => onOpen(t.avtaleId)}
              className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg px-2 py-2 text-left hover:bg-green-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">
                <span className="font-semibold text-slate-900">{t.fornavn} {t.etternavn}</span>
                {t.avdeling && <span className="text-slate-700"> · {t.avdeling}</span>}
              </span>
              <span className="inline-flex min-w-0 items-center gap-1 font-semibold text-green-900 [overflow-wrap:anywhere]">
                {navnPaAvtale.get(t.avtaleId) || "Avtale"} <Ikon navn="pil" className="w-5 h-5 shrink-0" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Avtaler() {
  const [agreements, setAgreements] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feil, setFeil] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fakturaFilter, setFakturaFilter] = useState("");
  const [showNewForm, setShowNewForm] = useState(false);
  const [ansattTreff, setAnsattTreff] = useState([]);
  const navigate = useNavigate();

  // Søk i ansattlistene på serveren, litt etter at man har sluttet å skrive.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setAnsattTreff([]);
      return undefined;
    }
    let aktiv = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/avtaler/ansatte/sok?q=${encodeURIComponent(q)}`);
        if (!r.ok) throw new Error();
        const data = await r.json();
        if (aktiv) setAnsattTreff(data);
      } catch {
        if (aktiv) setAnsattTreff([]);
      }
    }, 250);
    return () => { aktiv = false; clearTimeout(t); };
  }, [search]);

  useEffect(() => {
    async function fetchAgreements() {
      try {
        const r = await fetch(`${API_BASE_URL}/avtaler`);
        if (!r.ok) throw new Error();
        setAgreements(await r.json());
      } catch {
        setFeil("Klarte ikke å hente avtalene. Last siden på nytt om litt.");
      } finally {
        setLoading(false);
      }
    }
    async function fetchSettings() {
      try {
        const r = await fetch(`${API_BASE_URL}/avtaler/innstillinger`);
        if (!r.ok) throw new Error();
        setSettings(await r.json());
      } catch {
        console.error("Kunne ikke hente innstillinger");
      }
    }
    fetchAgreements();
    fetchSettings();
  }, []);

  // Antall per status, vist på filterknappene.
  const perStatus = useMemo(() => {
    const antall = { "": agreements.length };
    agreements.forEach((a) => {
      const key = getAgreementStatus(a.expiryDate).key;
      antall[key] = (antall[key] || 0) + 1;
    });
    return antall;
  }, [agreements]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return agreements.filter((a) => {
      const matchesSearch = !q || [a.name, a.discountDetails, a.discountCode, a.contactInfo, a.contactPhone]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
      const matchesType = !typeFilter || a.type === typeFilter;
      const matchesStatus = !statusFilter || getAgreementStatus(a.expiryDate).key === statusFilter;
      const matchesFaktura = !fakturaFilter || (fakturaFilter === "JA") === Boolean(a.fakturakunde);
      return matchesSearch && matchesType && matchesStatus && matchesFaktura;
    });
  }, [agreements, search, typeFilter, statusFilter, fakturaFilter]);

  const antallFaktura = agreements.filter((a) => a.fakturakunde).length;
  const filtrert = search.trim() || typeFilter || statusFilter || fakturaFilter;

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Tilbake />

      <Sidetopp ikon="handtrykk" farge="kunde" tittel="Avtaleoversikt" undertittel="Rabattavtaler med klubber, foreninger og bedrifter">
        <button
          type="button"
          onClick={() => setShowNewForm((v) => !v)}
          aria-expanded={showNewForm}
          className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-2xl border-2 border-sky-200 bg-sky-50 px-3 py-1.5 font-bold text-slate-900 transition hover:bg-sky-100 active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
        >
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${FARGER.kunde.ikon}`}>
            <Ikon navn="pluss" className="w-6 h-6" />
          </span>
          Ny avtale
        </button>
      </Sidetopp>

      {showNewForm && (
        <NewAgreementForm
          onCreated={(a) => { setAgreements((prev) => [...prev, a].sort((x, y) => x.name.localeCompare(y.name, "nb"))); setShowNewForm(false); }}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      {settings && <ExclusionBanner settings={settings} onSettingsUpdated={setSettings} />}

      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}

      <section aria-labelledby="av-alle" className="space-y-3">
        <h2 id="av-alle" className="sr-only">Avtalene</h2>

        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <div>
            <label htmlFor="av-sok" className="mb-1 block font-semibold text-slate-800">Søk i avtalene</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                <Ikon navn="sok" className="w-6 h-6" />
              </span>
              <input
                id="av-sok"
                type="search"
                inputMode="search"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Navn, rabatt, rabattkode, kontaktperson eller ansatt"
                aria-describedby="av-sok-status"
                className={`${feltKlasse} pl-12`}
              />
            </div>
          </div>
          <Valgknapper
            id="av-type-filter"
            etikett="Type"
            valg={[{ verdi: "", tittel: "Alle" }, ...TYPES.map((t) => ({ verdi: t, tittel: TYPE_LABELS[t] }))]}
            verdi={typeFilter}
            onVelg={setTypeFilter}
          />
          <Valgknapper
            id="av-status-filter"
            etikett="Status"
            valg={STATUS_FILTERS.map((s) => ({ verdi: s.key, tittel: `${s.label} (${perStatus[s.key] || 0})` }))}
            verdi={statusFilter}
            onVelg={setStatusFilter}
          />
          <Valgknapper
            id="av-faktura-filter"
            etikett="Fakturakunde"
            valg={[
              { verdi: "", tittel: "Alle" },
              { verdi: "JA", tittel: `Fakturakunde (${antallFaktura})` },
              { verdi: "NEI", tittel: `Ikke fakturakunde (${agreements.length - antallFaktura})` },
            ]}
            verdi={fakturaFilter}
            onVelg={setFakturaFilter}
          />
        </div>

        <p id="av-sok-status" role="status" className="text-slate-700 empty:hidden">
          {!loading && filtrert ? `${filtered.length} av ${agreements.length} avtaler vises` : ""}
          {ansattTreff.length > 0 ? `, ${ansattTreff.length} treff i ansattlistene` : ""}
        </p>

        <AnsattTreff treff={ansattTreff} avtaler={agreements} onOpen={(id) => navigate(`/avtaler/${id}`)} />

        {loading ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter avtaler …</p>
        ) : filtered.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
            {filtrert ? "Ingen avtaler passer søket eller filtrene." : "Ingen avtaler ennå."}
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((agreement) => (
              <li key={agreement._id} className="min-w-0">
                <AgreementCard agreement={agreement} onOpen={(id) => navigate(`/avtaler/${id}`)} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default Avtaler;
